import { Form, Head } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { agentPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property } from "@/types"

interface Props {
  property: Property
}

export default function New({ property }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: agentPropertiesPath() },
    { title: property.name, href: `/agent/properties/${property.id}` },
    { title: "Leasing Contacts", href: `/agent/properties/${property.id}/leasing_contacts` },
    { title: "New", href: `/agent/properties/${property.id}/leasing_contacts/new` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`New Leasing Contact — ${property.name}`} />

      <div className="max-w-2xl space-y-6 p-6">
        <h1 className="text-2xl font-semibold">Add Leasing Contact</h1>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Form
              method="post"
              action={`/agent/properties/${property.id}/leasing_contacts`}
              encType="multipart/form-data"
              className="space-y-4"
            >
              {({ errors, processing }) => (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" name="name" placeholder="Connor Sampson" />
                      <InputError messages={errors.name} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="title">Title</Label>
                      <Input id="title" name="title" placeholder="Leasing & Development" />
                      <InputError messages={errors.title} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" placeholder="csampson@treystar.com" />
                      <InputError messages={errors.email} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" name="phone" placeholder="269.329.1808" />
                      <InputError messages={errors.phone} />
                    </div>
                  </div>

                  <div className="grid max-w-xs gap-2">
                    <Label htmlFor="position">Display Order</Label>
                    <Input id="position" name="position" type="number" defaultValue={0} />
                    <p className="text-muted-foreground text-xs">Lower numbers appear first.</p>
                    <InputError messages={errors.position} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="headshot">Headshot</Label>
                    <Input id="headshot" name="headshot" type="file" accept="image/*" />
                    <p className="text-muted-foreground text-xs">Square image works best. Rendered as a circle on the brochure.</p>
                    <InputError messages={errors.headshot} />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" disabled={processing}>Add Contact</Button>
                    <Button variant="outline" type="button" onClick={() => history.back()}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
